import React, { useState } from 'react';
import {
  Badge,
  Button,
  Content,
  Flex,
  FlexItem,
  Form,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  Label,
  Popover,
  TextInput,
} from '@patternfly/react-core';
import { ActionsColumn, type IAction, Td, Tr } from '@patternfly/react-table';
import { CopyIcon, PencilAltIcon } from '@patternfly/react-icons';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { DECIMAL_RE, formatProductPrice, formatRecurring } from '../../../utils/money';
import type { AdminProduct } from '../../../types/models/AdminProduct';
import type { ApiResponseAdminProductDefaultPrice } from '../../../types/api/ApiResponseAdminProduct';

interface AdminProductRowProps {
  product: AdminProduct;
  /** Replaces this product's row in the parent list from a PUT response (no full refetch). */
  onUpdated: (updated: AdminProduct) => void;
  /** Opens the per-client overrides modal for this product (not offered for domain operations). */
  onManageOverrides: (product: AdminProduct) => void;
  /** Opens the full-modification modal for this product (label, descriptions, cadence, active, category). */
  onEdit: (product: AdminProduct) => void;
  /** Opens the create modal pre-filled with this product's cloneable fields (everything but the key). */
  onClone: (product: AdminProduct) => void;
}

function AdminProductRow({ product, onUpdated, onManageOverrides, onEdit, onClone }: AdminProductRowProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();

  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Domain operations (product_key starting with DOMAIN_) are matrix-priced (price = f(operation,
  // tld, client)) and live under a separate /admin/products/domains surface; the scalar price
  // endpoints reject them. The reliable signal is the key prefix — the row shows a non-editable note.
  const isDomainOp = product.product_key.startsWith('DOMAIN_');

  const overridesAriaLabel =
    product.override_count > 0
      ? `Manage ${product.override_count} client price${product.override_count === 1 ? '' : 's'} for ${product.label}`
      : `Add a client price for ${product.label}`;

  const rowActions: IAction[] = [
    {
      title: 'Modify',
      icon: <PencilAltIcon />,
      onClick: () => onEdit(product),
    },
    {
      title: 'Clone',
      icon: <CopyIcon />,
      onClick: () => onClone(product),
    },
  ];

  const trimmed = draft.trim();
  const isDraftValid = DECIMAL_RE.test(trimmed);
  const helperError =
    serverError ??
    (trimmed !== '' && !isDraftValid ? 'Enter a non-negative amount with up to 4 decimal places.' : null);

  const inputId = `admin-price-input-${product.id}`;

  const openEditor = () => {
    // Re-clicking the trigger while the editor is open first fires the Popover's native shouldClose
    // (which closes it); bail out here so the subsequent React onClick doesn't re-open and silently
    // discard the in-progress draft. From the closed state isEditing is false, so opening still works.
    if (isEditing) {
      return;
    }
    // Seed with the current default; empty when unpriced (the "Set price" flow).
    setDraft(product.default_unit_price ?? '');
    setServerError(null);
    setIsEditing(true);
  };

  const closeEditor = () => {
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!isDraftValid) {
      return;
    }
    setIsSaving(true);
    setServerError(null);
    try {
      const response = await apiFetch(`/admin/products/${product.id}/price`, {
        method: 'PUT',
        body: JSON.stringify({ unit_price: trimmed }),
      });
      // Branch on the JSend body.status, NEVER response.ok.
      const body = (await response.json().catch(() => null)) as ApiResponseAdminProductDefaultPrice | null;
      if (body?.status === 'success' && body.data?.price) {
        // The PUT returns only { product_id, default_unit_price }, so merge the new default into the
        // existing row rather than replacing the whole product.
        const newDefault = body.data.price.default_unit_price;
        onUpdated({ ...product, default_unit_price: newDefault });
        addNotification({
          title: 'Default price updated',
          srTitle: 'Success alert',
          description: `${product.label} default is now ${formatProductPrice(newDefault, product.recurring)} (excl. VAT).`,
          variant: 'success',
        });
        setDrawerOpen(true);
        setIsEditing(false);
      } else {
        // Surface the field-level error inline (keeping the editor open to fix it): a validation
        // failure lands in data.unit_price, a domain-operation rejection in data.product.
        const fieldError = body?.data?.unit_price ?? body?.data?.product ?? null;
        setServerError(fieldError);
        addNotification({
          title: 'Could not update price',
          srTitle: 'Error alert',
          description: fieldError ?? body?.message ?? 'Could not update the price. Please try again.',
          variant: 'danger',
        });
        setDrawerOpen(true);
      }
    } catch (err) {
      addNotification({
        title: 'Could not update price',
        srTitle: 'Error alert',
        description: err instanceof Error ? err.message : 'An unexpected error occurred.',
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  const editorBody = (
    <Form
      onSubmit={(event) => {
        event.preventDefault();
        void handleSave();
      }}
    >
      {product.product_desc && (
        <div className="pf-v6-u-mb-sm">
          <Content component="small">{product.product_desc}</Content>
        </div>
      )}
      <FormGroup label="Default price (excl. VAT)" fieldId={inputId}>
        <TextInput
          id={inputId}
          type="text"
          inputMode="decimal"
          value={draft}
          onChange={(_event, value) => {
            setDraft(value);
            // Drop a prior backend field error the moment the user edits, so the inline helper
            // reflects the current input rather than a stale rejection.
            if (serverError) {
              setServerError(null);
            }
          }}
          validated={helperError ? 'error' : 'default'}
          isDisabled={isSaving}
          aria-label="Default unit price in euros, excluding VAT"
        />
        {helperError && (
          <FormHelperText>
            <HelperText>
              <HelperTextItem variant="error">{helperError}</HelperTextItem>
            </HelperText>
          </FormHelperText>
        )}
      </FormGroup>
      <Flex spaceItems={{ default: 'spaceItemsSm' }}>
        <FlexItem>
          <Button type="submit" variant="primary" isLoading={isSaving} isDisabled={isSaving || !isDraftValid}>
            Save
          </Button>
        </FlexItem>
        <FlexItem>
          <Button variant="link" onClick={closeEditor} isDisabled={isSaving}>
            Cancel
          </Button>
        </FlexItem>
      </Flex>
    </Form>
  );

  const renderPriceCell = () => {
    if (isDomainOp) {
      return <Content component="small">Priced per TLD</Content>;
    }
    return (
      <Popover
        isVisible={isEditing}
        shouldClose={closeEditor}
        headerContent={`Set default price — ${product.label}`}
        bodyContent={editorBody}
        aria-label="Edit default price"
        // Focus the price input on open (the focus trap would otherwise land on the close button),
        // so the user can type immediately and Enter submits.
        elementToFocus={`#${inputId}`}
      >
        <Button variant="link" isInline onClick={openEditor} aria-label={`Edit default price for ${product.label}`}>
          {product.default_unit_price !== null
            ? formatProductPrice(product.default_unit_price, product.recurring)
            : 'Set price'}
        </Button>
      </Popover>
    );
  };

  return (
    <Tr>
      <Td dataLabel="Product">
        <div>{product.label}</div>
        <Content component="small">{product.product_key}</Content>
      </Td>
      <Td dataLabel="Billing">{formatRecurring(product.recurring)}</Td>
      <Td dataLabel="Default price">{renderPriceCell()}</Td>
      <Td dataLabel="Overrides">
        {isDomainOp ? (
          <Content component="small">—</Content>
        ) : (
          <Button variant="link" isInline onClick={() => onManageOverrides(product)} aria-label={overridesAriaLabel}>
            {product.override_count > 0 ? (
              <>
                <Badge isRead>{product.override_count}</Badge> Manage
              </>
            ) : (
              'Add'
            )}
          </Button>
        )}
      </Td>
      <Td dataLabel="Active">
        {product.active ? (
          <Label color="green" isCompact>
            Active
          </Label>
        ) : (
          <Label color="grey" isCompact>
            Retired
          </Label>
        )}
      </Td>
      <Td dataLabel="Visibility">
        {product.tenant_visible ? (
          <Label color="blue" isCompact>
            Visible
          </Label>
        ) : (
          // Hidden-but-billable (tenant_visible = false): off every customer pricing surface while
          // deals/billing keep working — the usual posture for per-deal rebate products.
          <Label color="grey" isCompact>
            Hidden
          </Label>
        )}
      </Td>
      <Td isActionCell>
        <ActionsColumn items={rowActions} />
      </Td>
    </Tr>
  );
}

export default AdminProductRow;
